import { endOfDay, startOfDay, subDays, addDays } from "date-fns";
import has from "lodash/has";
import { filterActivitiesForRotas } from "@/lib/rota/activity-functions";

const API_NAME = "rota";
const API_VERSION = "/v1";
const CONTENT_TYPE = "application/vnd.coactive.rota-activity+json";
const MAX_PAGE_SIZE = 1000;

const actions = {
  async getRotaActivity(context, params) {
    const myInit = {};
    myInit.queryStringParameters = {
      withRotaShifts: true,
    };
    return this.$api.get(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.id}`,
      myInit,
    );
  },

  async getRotaActivities(context, payload) {
    const myInit = {};
    // payload contains filter/sort options.
    if (payload) {
      myInit.queryStringParameters = {
        page: payload.page,
        pageSize: payload.pageSize ? payload.pageSize : MAX_PAGE_SIZE,
      };

      if (has(payload, "sortBy")) {
        let sortField = payload.sortBy;
        if (sortField === "dateCreated") {
          sortField = "_id";
        }
        let sortOrder = 1;
        if (has(payload, "sortDesc") && payload.sortDesc === true) {
          sortOrder = -1;
        }
        myInit.queryStringParameters.sort = JSON.stringify({
          [sortField]: sortOrder,
        });
      }

      if (has(payload, "filter")) {
        myInit.queryStringParameters.filter = JSON.stringify(payload.filter);
      }
    }
    return this.$api
      .get(API_NAME, `${API_VERSION}/rota-activities`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rota-activities")) {
          return response;
        }
        return false;
      });
  },
  /**
   * Retrieves recent adhoc activities for a specific client.
   *
   * @param {Object} context
   * @param {string} clientId - The ID of the client to retrieve activities for.
   * @returns {Promise<Array|boolean>} An array of adhoc activities for the client, or false if no activities were found.
   */
  async getRecentAdhocActivitiesForClients(context, clientId) {
    const myInit = {};

    const today = new Date();
    const thirtyDaysAgo = subDays(today, 30);

    myInit.queryStringParameters = {
      sort: JSON.stringify({ startDate: -1 }),
      pageSize: 5,
      page: 1,
      filter: JSON.stringify([
        { subject: "clients.clientId", comparison: "eq", value: clientId },
        { subject: "adhoc", comparison: "eq", value: true },
        {
          subject: "startDate",
          comparison: "between-inc",
          value: [thirtyDaysAgo, today],
        },
      ]),
    };
    return this.$api
      .get(API_NAME, `${API_VERSION}/rota-activities`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rota-activities")) {
          return response._embedded["rota-activities"];
        }
        return false;
      });
  },
  /**
   * Fetches activities relevant to a particular rota
   * - includes activities created by the rota and linked to the rota
   * - includes PCS activities not linked to this rota
   * - excludes any activities linked to OTHER rotas
   *
   * @param {*} context
   * @param {*} rota
   * @returns
   */
  async getActivitiesForRota(context, rota) {
    const myInit = {};
    const myFilter = [
      {
        subject: "locationId",
        comparison: "eq",
        value: rota.location,
      },
      {
        subject: "startDate",
        comparison: "between-inc",
        value: [rota.startDate, addDays(rota.endDate, 1)],
      },
    ];
    if (rota.status === "master") {
      myFilter.push(
        {
          subject: "master",
          comparison: "eq",
          value: true,
        },
        // CC-1837 master rota activities are linked to the rota by their rotaId prop.
        // We don't filter by rotaId normally since we also want to pick up PCS activities
        {
          subject: "rotaId",
          comparison: "eq",
          value: rota.id,
        },
      );
    }
    myInit.queryStringParameters = {
      pageSize: MAX_PAGE_SIZE,
      filter: JSON.stringify(myFilter),
    };
    return this.$api
      .getAllPages(API_NAME, `${API_VERSION}/rota-activities`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rota-activities")) {
          return filterActivitiesForRotas(
            response._embedded["rota-activities"],
            [rota],
          );
        }
        return false;
      });
  },
  async getActivitiesForYesterdayAndNextDay({ dispatch }, payload) {
    return dispatch("getActivitiesForRange", {
      startDate: startOfDay(subDays(payload.day, 1)),
      endDate: endOfDay(addDays(payload.day, 1)),
    });
  },
  async getActivitiesForPreviousWeekAndNextDay({ dispatch }, payload) {
    return dispatch("getActivitiesForRange", {
      startDate: startOfDay(subDays(payload.day, 8)),
      endDate: endOfDay(addDays(payload.day, 1)),
    });
  },
  async getActivitiesForRange(context, payload) {
    const myInit = {};
    const myFilter = [
      {
        subject: "startDate",
        comparison: "between-inc",
        value: [payload.startDate, payload.endDate],
      },
    ];
    if (payload.client && payload.client !== null) {
      myFilter.push({
        subject: "clients.clientId",
        comparison: "eq",
        value: payload.client.id,
      });
    }
    if (payload.reportEvent && payload.reportEvent !== null) {
      myFilter.push({
        subject: "clients.reports.event",
        comparison: "in",
        value: Array.isArray(payload.reportEvent)
          ? payload.reportEvent
          : [payload.reportEvent],
      });
    }
    myInit.queryStringParameters = {
      pageSize: MAX_PAGE_SIZE,
      filter: JSON.stringify(myFilter),
      sort: JSON.stringify({ startDate: 1 }),
    };
    const response = await this.$api.getAllPages(
      API_NAME,
      `${API_VERSION}/rota-activities`,
      myInit,
    );
    if (has(response, "_embedded.rota-activities")) {
      return response._embedded["rota-activities"];
    }
    return false;
  },
  async getActivitiesForRangeWithReports(context, payload) {
    const myInit = {};
    const myFilter = [
      {
        subject: "startDate",
        comparison: "between-inc",
        value: [payload.startDate, payload.endDate],
      },
    ];
    if (payload.location) {
      myFilter.push({
        subject: "locationId",
        comparison: "eq",
        value: payload.location,
      });
    }
    if (payload.client && payload.client !== null) {
      myFilter.push({
        subject: "clients.clientId",
        comparison: "eq",
        value: payload.client.id,
      });
    }
    if (payload.reportEvent && payload.reportEvent !== null) {
      myFilter.push({
        subject: "clients.reports.event",
        comparison: "in",
        value: Array.isArray(payload.reportEvent)
          ? payload.reportEvent
          : [payload.reportEvent],
      });
    }
    myInit.queryStringParameters = {
      pageSize: MAX_PAGE_SIZE,
      filter: JSON.stringify(myFilter),
      sort: JSON.stringify({ startDate: 1 }),
      reportsOnly: 1,
    };
    return this.$api
      .getAllPages(API_NAME, `${API_VERSION}/rota-activities`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rota-activities")) {
          return response._embedded["rota-activities"];
        }
        return false;
      });
  },
  async createRotaActivity(context, rotaActivity) {
    const api = this.$api;
    if (rotaActivity.doNotCatchIfError) {
      api.doNotCatchIfError();
      delete rotaActivity.doNotCatchIfError;
    }
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: rotaActivity,
    };

    return api.post(API_NAME, `${API_VERSION}/rota-activities`, myInit);
  },
  async updateRotaActivity(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.changes,
    };
    return this.$api.patch(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.id}`,
      myInit,
    );
  },
  async deleteRotaActivity(context, params) {
    return this.$api.del(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.id}`,
    );
  },
  async sendHelpRequest(context, params) {
    const myInit = {
      body: {
        message: params.message,
      },
    };
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.id}/request-help`,
      myInit,
    );
  },

  async reportChallengingBehaviour(context, params) {
    const myInit = {
      body: params.body,
    };

    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.activityId}/clients/${params.clientId}/reports/challenging-behaviour`,
      myInit,
    );
  },
  async confirmIncidentReportFiled(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
    };
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.activityId}/clients/${params.clientId}/reports/challenging-behaviour/${params.reportId}/incident-report-filed`,
      myInit,
    );
  },
  async getUnfiledIncidentReports() {
    return this.$api.get(
      API_NAME,
      `${API_VERSION}/rota-activities/reports/challenging-behaviour/unfiled-incident-reports`,
    );
  },

  async getActivitiesWithRetry() {
    return this.$api
      .get(API_NAME, `${API_VERSION}/rota-activities/need-retry`)
      .then((response) => {
        if (has(response, "_embedded.rota-activities")) {
          return response._embedded["rota-activities"];
        }
        return false;
      });
  },

  async getInProgressActivities() {
    return this.$api
      .get(API_NAME, `${API_VERSION}/rota-activities/in-progress`)
      .then((response) => {
        if (has(response, "_embedded.rota-activities")) {
          return response._embedded["rota-activities"];
        }
        return [];
      });
  },

  async getActivitiesForUser(context, params) {
    const myInit = {
      queryStringParameters: {
        includeUnassigned: "true",
      },
    };
    if (params.startDate) {
      myInit.queryStringParameters.startDate = params.startDate;
    }
    if (params.endDate) {
      myInit.queryStringParameters.endDate = params.endDate;
    }
    if (params.includeUnassigned) {
      myInit.queryStringParameters.includeUnassigned = params.includeUnassigned;
    }

    return this.$api
      .get(API_NAME, `${API_VERSION}/rota-activities/for-user`, myInit)
      .then((response) => {
        if (has(response, "_embedded.rota-activities")) {
          return response._embedded["rota-activities"];
        }
        return [];
      });
  },

  async startActivity(context, params) {
    const myInit = {
      body: params.body,
    };

    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.activityId}/start`,
      myInit,
    );
  },
  async createUnableToStartActivityReport(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.body,
    };
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.activityId}/unavailable`,
      myInit,
    );
  },
  async createCommentActivityReport(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.body,
    };

    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.activityId}/comment`,
      myInit,
    );
  },
  async createContinuationActivityReport(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.body,
    };

    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.activityId}/continuation`,
      myInit,
    );
  },
  async stopActivity(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.body,
    };

    return this.$api.post(
      API_NAME,
      `${API_VERSION}/rota-activities/${params.activityId}/stop`,
      myInit,
    );
  },

  async getActivityStatsForClientEnjoyment(context, params) {
    const myInit = {
      queryStringParameters: {
        startDate: startOfDay(params.startDate),
        endDate: endOfDay(params.endDate),
      },
    };
    if (params.clientId) {
      myInit.queryStringParameters.clientId = params.clientId;
    }

    return this.$api.get(
      API_NAME,
      `${API_VERSION}/stats/rota-activities/client-enjoyment`,
      myInit,
    );
  },
};

export default {
  namespaced: true,
  actions,
};
